/**
 * Center Book sidebar — five compact widgets that turn the right-rail
 * slot (previously a mostly-empty leaderboard) into a portfolio-level
 * dashboard.
 *
 * Visual language follows TradingView density principles: small caps
 * labels, tabular numerics, thin horizontal bars, tooltip on every
 * non-trivial element so operators can learn the dashboard without
 * docs.  All five widgets are computed from data already in the
 * store (no backend changes) -- see ``state/centerbookSelectors`` for
 * the pure-math derivations.
 *
 * Sections:
 *   1. Capital allocation -- pod_share stacked bar with legend
 *   2. Risk-state breakdown -- 4-segment capital pie by tier
 *   3. Position concentration -- top tickers held by 2+ pods
 *   4. Daily P&L distribution -- horizontal bars per pod, sorted
 *   5. Alerts roll-up -- total + per-pod tally
 */
import { useMemo, useState } from 'react';

import type { FloorView } from '@/api/rest';
import { Tooltip } from '@/components/Tooltip';
import { useIsMobile } from '@/hooks/useBreakpoint';
import {
  formatMillions,
  formatPct,
  signedM,
  signedPct,
} from '@/state/centerbookFormat';
import { useDashboardStore } from '@/state/store';
import {
  selectAlertsRollup,
  selectCapitalFlowEvents,
  selectCapitalMap,
  selectCorrelationMatrix,
  selectPnlDistribution,
  selectRiskBreakdown,
  selectTickerConcentration,
  type CapitalFlowEvent,
  type CorrelationMatrix,
  type RiskState,
  type TickerConcentration,
} from '@/state/centerbookSelectors';

const EMPTY_FLOORS: readonly FloorView[] = [];

/** Per-risk-state accent.  Matches the building-view pod-window
 *  border colours so users associate the same hue with the same
 *  risk tier everywhere on the dashboard. */
const RISK_COLOR: Record<RiskState, string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

const RISK_LABEL: Record<RiskState, string> = {
  active: 'active',
  watch: 'watch',
  reduced: 'reduced',
  frozen: 'frozen',
};

/** Stable colour palette for the capital-allocation stacked bar.  Falls
 *  back to risk-state colour when we run out of distinct hues. */
const POD_PALETTE = ['#3fb950', '#58a6ff', '#a371f7', '#f78166', '#ffd33d', '#bc8cff', '#7ee787'];

export function CenterBookPanel() {
  const isMobile = useIsMobile();
  const floors = useDashboardStore((s) => s.building?.floors ?? EMPTY_FLOORS);
  const pods = useDashboardStore((s) => s.pods);
  // Flatten all pods from the building snapshot.  ``useMemo`` because
  // the array is created fresh on each store change and we want
  // selector outputs to stay referentially stable when the underlying
  // data hasn't moved.
  const summaries = useMemo(() => floors.flatMap((f) => f.pods), [floors]);
  const podNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of summaries) m[p.pod_id] = p.name;
    return m;
  }, [summaries]);

  const capitalMap = useMemo(() => selectCapitalMap(summaries), [summaries]);
  const riskBreakdown = useMemo(() => selectRiskBreakdown(summaries), [summaries]);
  const concentration = useMemo(
    () => selectTickerConcentration(pods, podNames),
    [pods, podNames],
  );
  const pnl = useMemo(() => selectPnlDistribution(summaries), [summaries]);
  const alerts = useMemo(() => selectAlertsRollup(pods, podNames), [pods, podNames]);
  const correlation = useMemo(
    () =>
      selectCorrelationMatrix(
        summaries.map((p) => p.pod_id),
        pods,
      ),
    [summaries, pods],
  );
  const feed = useDashboardStore((s) => s.feed);
  const capitalFlow = useMemo(
    () => selectCapitalFlowEvents(feed, podNames),
    [feed, podNames],
  );

  if (summaries.length === 0) {
    return (
      <aside
        aria-label="Center Book"
        style={{
          padding: 14,
          background: '#0d1117',
          border: '1px solid #21262d',
          borderRadius: 12,
          color: '#6e7681',
          fontSize: 12,
        }}
      >
        Загрузка данных по подам…
      </aside>
    );
  }

  return (
    <aside
      aria-label="Center Book"
      style={{
        background: '#0d1117',
        border: '1px solid #21262d',
        borderRadius: 12,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        fontSize: 11,
        minWidth: 200,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Tooltip content="Центральная книга — агрегаты по всем подам: распределение капитала, риски, концентрация позиций и P&L.">
          <strong
            style={{
              fontSize: 12,
              letterSpacing: 0.5,
              color: '#e6edf3',
            }}
          >
            Center Book
          </strong>
        </Tooltip>
        <Tooltip
          content={[
            `Всего: ${summaries.length} подов`,
            `Общий капитал: ₽${formatMillions(capitalMap.total)}`,
          ]}
        >
          <span style={{ fontSize: 10, color: '#6e7681' }}>
            {summaries.length} pods · ₽{formatMillions(capitalMap.total)}
          </span>
        </Tooltip>
      </header>

      <CapitalAllocationSection result={capitalMap} collapsible={isMobile} />
      <PnlSection distribution={pnl} collapsible={isMobile} />
      <AlertsSection rollup={alerts} collapsible={isMobile} />
      <RiskBreakdownSection breakdown={riskBreakdown} collapsible={isMobile} />
      <ConcentrationSection rows={concentration} collapsible={isMobile} />
      <CorrelationMatrixSection
        matrix={correlation}
        podNames={podNames}
        collapsible={isMobile}
        isMobile={isMobile}
      />
      <CapitalFlowSection events={capitalFlow} collapsible={isMobile} />
    </aside>
  );
}

// =================================================================
// Section 1: Capital allocation (stacked bar)
// =================================================================

export function CapitalAllocationSection({
  result,
  collapsible,
}: {
  result: ReturnType<typeof selectCapitalMap>;
  collapsible: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  if (result.total <= 0) return null;
  const showBody = !collapsible || expanded;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Capital allocation"
        tooltip="Доля капитала каждого пода относительно общего пула. Capital — это base размер пода (без P&L), назначается центр-буком."
        toggle={collapsible ? () => setExpanded((e) => !e) : undefined}
        expanded={expanded}
      />
      {showBody ? (
        <>
          {/* Stacked bar */}
          <div
            style={{
              display: 'flex',
              height: 14,
              borderRadius: 4,
              overflow: 'hidden',
              background: '#161b22',
              border: '1px solid #21262d',
            }}
          >
            {result.slots.map((slot, i) => (
              <Tooltip
                key={slot.podId}
                content={[
                  slot.name,
                  `Доля: ${formatPct(slot.share, 1)}`,
                  `Капитал: ₽${formatMillions(slot.capital)}`,
                  slot.dailyReturn != null ? `Сегодня: ${signedPct(slot.dailyReturn)}` : '',
                  `Risk: ${slot.riskState}`,
                ]}
                style={{
                  width: `${slot.share * 100}%`,
                  background: POD_PALETTE[i % POD_PALETTE.length],
                  display: 'block',
                }}
              >
                <span style={{ display: 'block', width: '100%', height: '100%' }} />
              </Tooltip>
            ))}
          </div>
          {/* Legend */}
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {result.slots.map((slot, i) => (
              <Tooltip
                key={slot.podId}
                as="div"
                content={[
                  slot.name,
                  `Доля: ${formatPct(slot.share, 1)} · ₽${formatMillions(slot.capital)}`,
                  `Risk: ${slot.riskState}`,
                  slot.dailyPnl != null ? `P&L: ${signedM(slot.dailyPnl)}` : '',
                ]}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '10px 1fr auto',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 10,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: POD_PALETTE[i % POD_PALETTE.length],
                  }}
                />
                <span
                  style={{
                    color: '#c9d1d9',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {slot.name}
                </span>
                <span
                  style={{
                    color: '#8b949e',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatPct(slot.share, 0)}
                </span>
              </Tooltip>
            ))}
          </ol>
        </>
      ) : null}
    </section>
  );
}

// =================================================================
// Section 2: Risk state breakdown
// =================================================================

export function RiskBreakdownSection({
  breakdown,
  collapsible,
}: {
  breakdown: ReturnType<typeof selectRiskBreakdown>;
  collapsible: boolean;
}) {
  const tiers: RiskState[] = ['active', 'watch', 'reduced', 'frozen'];
  // Hide entire section when every pod is active — nothing interesting yet.
  const anyNonActive =
    breakdown.buckets.watch.count +
      breakdown.buckets.reduced.count +
      breakdown.buckets.frozen.count >
    0;
  // On mobile default-expand only when something interesting changed
  // (non-active tier present); otherwise it's a "all green" placeholder.
  const [expanded, setExpanded] = useState(!collapsible || anyNonActive);
  const showBody = !collapsible || expanded;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Risk states"
        tooltip="Сколько подов в каждом из четырёх режимов риска. Active — нормально торгуют; watch — наблюдение; reduced — урезанный gross; frozen — заморожены risk officer-ом."
        toggle={collapsible ? () => setExpanded((e) => !e) : undefined}
        expanded={expanded}
      />
      {!showBody ? null : (<>
      {/* Stacked bar by capital share */}
      {breakdown.totalCapital > 0 ? (
        <div
          style={{
            display: 'flex',
            height: 10,
            borderRadius: 3,
            overflow: 'hidden',
            background: '#161b22',
            border: '1px solid #21262d',
          }}
        >
          {tiers.map((tier) => {
            const b = breakdown.buckets[tier];
            const w = (b.capital / breakdown.totalCapital) * 100;
            if (w <= 0) return null;
            return (
              <Tooltip
                key={tier}
                content={[
                  `${RISK_LABEL[tier]} — ${b.count} pods`,
                  `Капитал: ₽${formatMillions(b.capital)} (${formatPct(b.capital / breakdown.totalCapital, 1)})`,
                ]}
                style={{ width: `${w}%`, background: RISK_COLOR[tier], display: 'block' }}
              >
                <span style={{ display: 'block', width: '100%', height: '100%' }} />
              </Tooltip>
            );
          })}
        </div>
      ) : null}
      {/* Per-tier rows */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 4,
          fontSize: 10,
        }}
      >
        {tiers.map((tier) => {
          const b = breakdown.buckets[tier];
          const muted = b.count === 0 && tier !== 'active';
          if (muted) return null;
          return (
            <Tooltip
              key={tier}
              as="div"
              content={[
                `${RISK_LABEL[tier]} · ${b.count} pod${b.count === 1 ? '' : 's'}`,
                b.podIds.length > 0 ? b.podIds.join(', ') : '(нет подов)',
              ]}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                padding: '2px 4px',
                borderRadius: 3,
                background: '#161b22',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: RISK_COLOR[tier],
                  flexShrink: 0,
                }}
              />
              <span style={{ color: '#c9d1d9', flex: 1 }}>{RISK_LABEL[tier]}</span>
              <span
                style={{
                  color: '#e6edf3',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {b.count}
              </span>
            </Tooltip>
          );
        })}
      </div>
      {!anyNonActive ? (
        <div style={{ fontSize: 10, color: '#6e7681' }}>
          Все поды в нормальном режиме.
        </div>
      ) : null}
      </>)}
    </section>
  );
}

// =================================================================
// Section 3: Position concentration heatmap
// =================================================================

export function ConcentrationSection({
  rows,
  collapsible,
}: {
  rows: TickerConcentration[];
  collapsible: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const showBody = !collapsible || expanded;
  const toggle = collapsible ? () => setExpanded((e) => !e) : undefined;
  if (rows.length === 0) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          title="Concentration"
          tooltip="Тикеры в которых сидят 2+ пода. Если несколько подов держат одну и ту же позицию — это концентрационный риск, фактор-краудинг."
          toggle={toggle}
          expanded={expanded}
        />
        {showBody ? (
          <div style={{ fontSize: 10, color: '#6e7681' }}>
            Пересечений по тикерам пока нет.
          </div>
        ) : null}
      </section>
    );
  }
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Concentration"
        tooltip="Тикеры в которых сидят 2+ пода. Hot tickers = риск факторного краудинга — если регион сольётся, потеряют все сразу. Netted = поды в противоположных сторонах (длинный + короткий), внутренний хедж."
        toggle={toggle}
        expanded={expanded}
        right={
          collapsible ? (
            <span
              style={{
                color: '#8b949e',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
              }}
            >
              {rows.length}
            </span>
          ) : null
        }
      />
      {showBody ? (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {rows.map((row) => (
            <ConcentrationRow key={row.ticker} row={row} />
          ))}
        </ol>
      ) : null}
    </section>
  );
}

function ConcentrationRow({ row }: { row: TickerConcentration }) {
  // Heat: 2 pods green-yellow, 3 yellow, 4+ red. Caps risk attention
  // matches user mental model of "crowd".
  const heat =
    row.holders.length >= 4 ? '#f85149' : row.holders.length === 3 ? '#d29922' : '#a371f7';
  const tipLines: string[] = [
    `${row.ticker} — держат ${row.holders.length} подов`,
    ...row.holders.map((h) => {
      const notional = h.notional != null ? ` (${signedM(h.notional)})` : '';
      return `  · ${h.podName} ${signedPct(h.weight)}${notional}`;
    }),
  ];
  if (row.netted) {
    tipLines.push('Netted: есть и long и short — внутренний хедж');
  }
  return (
    <Tooltip content={tipLines} as="div" maxWidth={320}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto auto',
          alignItems: 'center',
          gap: 6,
          padding: '2px 4px',
          borderRadius: 3,
          background: '#161b22',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: heat,
          }}
        />
        <span
          style={{
            color: '#e6edf3',
            fontFamily: 'ui-monospace, monospace',
            fontSize: 10,
          }}
        >
          {row.ticker}
        </span>
        <span
          style={{
            color: heat,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ×{row.holders.length}
        </span>
        <span
          style={{
            color: row.netted ? '#a371f7' : '#8b949e',
            fontSize: 9,
          }}
        >
          {row.netted ? 'netted' : '₽' + formatMillions(row.totalNotional)}
        </span>
      </div>
    </Tooltip>
  );
}

// =================================================================
// Section 4: Daily P&L distribution
// =================================================================

export function PnlSection({
  distribution,
  collapsible,
}: {
  distribution: ReturnType<typeof selectPnlDistribution>;
  collapsible: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const showBody = !collapsible || expanded;
  const toggle = collapsible ? () => setExpanded((e) => !e) : undefined;
  if (distribution.rows.length === 0) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          title="Daily P&L"
          tooltip="P&L каждого пода за текущий день. Зелёные — в плюсе, красные — в минусе. Aggregate — сумма по всему фонду."
          toggle={toggle}
          expanded={expanded}
        />
        {showBody ? (
          <div style={{ fontSize: 10, color: '#6e7681' }}>Нет данных по P&L.</div>
        ) : null}
      </section>
    );
  }
  const aggregateColor =
    distribution.aggregateNet > 0
      ? '#3fb950'
      : distribution.aggregateNet < 0
        ? '#f85149'
        : '#8b949e';
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Daily P&L"
        tooltip="P&L каждого пода за текущий день. Шкала бара — по самому большому |P&L|.  Aggregate = сумма по всему фонду."
        toggle={toggle}
        expanded={expanded}
        right={
          <Tooltip
            content={[
              `Aggregate net P&L по всему фонду: ${signedM(distribution.aggregateNet)}`,
              `Подов с данными: ${distribution.rows.length}`,
            ]}
          >
            <span
              style={{
                color: aggregateColor,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
              }}
            >
              {signedM(distribution.aggregateNet)}
            </span>
          </Tooltip>
        }
      />
      {showBody ? (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {distribution.rows.map((row) => (
            <PnlRow key={row.podId} row={row} maxAbs={distribution.maxAbs} />
          ))}
        </ol>
      ) : null}
    </section>
  );
}

function PnlRow({
  row,
  maxAbs,
}: {
  row: ReturnType<typeof selectPnlDistribution>['rows'][number];
  maxAbs: number;
}) {
  const positive = row.dailyPnl >= 0;
  const accent = positive ? '#3fb950' : '#f85149';
  const widthPct = maxAbs > 0 ? (Math.abs(row.dailyPnl) / maxAbs) * 100 : 0;
  const tipLines = [
    row.name,
    `P&L: ${signedM(row.dailyPnl)}${row.dailyReturn != null ? ` (${signedPct(row.dailyReturn)})` : ''}`,
    `Capital: ₽${formatMillions(row.capital)}`,
    `Risk: ${row.riskState}`,
  ];
  return (
    <Tooltip
      content={tipLines}
      as="div"
      style={{
        display: 'grid',
        gridTemplateColumns: '60px 1fr auto',
        alignItems: 'center',
        gap: 6,
        fontSize: 10,
      }}
    >
      <span
        style={{
          color: '#c9d1d9',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.name}
      </span>
      {/* Centred bar: zero in the middle, bar grows left or right. */}
      <span
        style={{
          position: 'relative',
          height: 6,
          background: '#161b22',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: positive ? '50%' : `${50 - widthPct / 2}%`,
            width: `${widthPct / 2}%`,
            height: '100%',
            background: accent,
          }}
        />
        {/* Centre tick. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: 1,
            height: '100%',
            background: '#30363d',
          }}
        />
      </span>
      <span
        style={{
          color: accent,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {signedM(row.dailyPnl)}
      </span>
    </Tooltip>
  );
}

// =================================================================
// Section 5: Alerts roll-up
// =================================================================

export function AlertsSection({
  rollup,
  collapsible,
}: {
  rollup: ReturnType<typeof selectAlertsRollup>;
  collapsible: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const showBody = !collapsible || expanded;
  const toggle = collapsible ? () => setExpanded((e) => !e) : undefined;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Alerts"
        tooltip="Кумулятивный счётчик ошибок по подам с момента открытия страницы: order.rejected, risk.veto, discipline.gate_blocked_order."
        toggle={toggle}
        expanded={expanded}
        right={
          <Tooltip
            content={[
              `Всего ошибок: ${rollup.totalErrors}`,
              rollup.totalErrors > 0
                ? `Подов с alerts: ${rollup.rows.length}`
                : 'Все поды работают без сбоев',
            ]}
          >
            <span
              style={{
                color: rollup.totalErrors > 0 ? '#f85149' : '#3fb950',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
              }}
            >
              {rollup.totalErrors}
            </span>
          </Tooltip>
        }
      />
      {!showBody ? null : rollup.rows.length === 0 ? (
        <div style={{ fontSize: 10, color: '#6e7681' }}>Все поды чисто.</div>
      ) : (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {rollup.rows.map((row) => {
            const tipLines = [
              `${row.name} · ${row.errorCount} alert${row.errorCount === 1 ? '' : 's'}`,
              row.lastKind
                ? `Последняя: ${row.lastKind.replace(/_/g, ' ')}${row.lastTicker ? ` (${row.lastTicker})` : ''}`
                : '',
              row.lastReason ? `Причина: ${row.lastReason}` : '',
            ];
            return (
              <Tooltip
                key={row.podId}
                content={tipLines}
                as="div"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'baseline',
                  gap: 6,
                  fontSize: 10,
                  padding: '2px 4px',
                  borderRadius: 3,
                  background: '#161b22',
                }}
              >
                <span
                  style={{
                    color: '#c9d1d9',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.name}
                </span>
                <span
                  style={{
                    color: '#f85149',
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {row.errorCount}
                </span>
              </Tooltip>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// =================================================================
// Shared section header
// =================================================================

// =================================================================
// Section 6: Correlation matrix (cosine, position-overlap)
// =================================================================

/** Discrete colour ramp for matrix cells.  Operators reading this
 *  fast should see "red = trouble", so we pick colours that map
 *  intuitively rather than a smooth gradient.  Diagonal cells use a
 *  faded mid-tone -- the 1.0 self-correlation isn't actionable. */
function corrColor(corr: number | null): string {
  if (corr === null) return '#21262d';
  const abs = Math.abs(corr);
  if (abs >= 0.7) return corr > 0 ? '#f85149' : '#a371f7';
  if (abs >= 0.4) return corr > 0 ? '#d29922' : '#7e57c2';
  if (abs >= 0.2) return corr > 0 ? '#bf8700' : '#5c4d8a';
  return '#30363d';
}

/** Three-character compact label for axis ticks.  Strips the
 *  ``pod_moex_`` / ``MOEX `` prefix and grabs the first 3 letters
 *  of the next word.  Falls back to the first 3 chars of the
 *  full name when nothing recognisable is left. */
function shortPodLabel(name: string): string {
  const stripped = name
    .replace(/^MOEX\s+/i, '')
    .replace(/\s+Pod$/i, '')
    .replace(/^pod_moex_/i, '')
    .replace(/_/g, ' ')
    .trim();
  const first = stripped.split(/\s+/)[0] ?? stripped;
  return first.slice(0, 3).toUpperCase();
}

export function CorrelationMatrixSection({
  matrix,
  podNames,
  collapsible,
  isMobile,
}: {
  matrix: CorrelationMatrix;
  podNames: Record<string, string>;
  collapsible: boolean;
  isMobile: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const showBody = !collapsible || expanded;
  const toggle = collapsible ? () => setExpanded((e) => !e) : undefined;
  // Matrix cells need a bigger tap-target on touch screens (14px is
  // unusable with a finger). 22px on mobile, 14px on desktop.
  const cellHeight = isMobile ? 22 : 14;
  if (matrix.podIds.length < 2) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          title="Correlation"
          tooltip="Pairwise сходство портфелей: для каждой пары подов считаем cosine similarity их weight-векторов (по позициям). +1 = идентичные книги, 0 = независимые, -1 = одна длинная, другая короткая в тех же тикерах."
          toggle={toggle}
          expanded={expanded}
        />
        {showBody ? (
          <div style={{ fontSize: 10, color: '#6e7681' }}>Нужно минимум 2 пода.</div>
        ) : null}
      </section>
    );
  }
  const labels = matrix.podIds.map((id) => shortPodLabel(podNames[id] ?? id));
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Correlation"
        tooltip="Pairwise сходство портфелей: cosine similarity weight-векторов. +1 = идентичные книги (factor crowding), 0 = независимые, -1 = противоположные (внутренний хедж). Цвет: красный ≥0.7, жёлтый ≥0.4, серый <0.2, фиолетовый — отрицательная (хедж)."
        toggle={toggle}
        expanded={expanded}
        right={
          matrix.maxCorrelation != null ? (
            <Tooltip
              content={[
                `Самая высокая pairwise correlation: ${matrix.maxCorrelation.toFixed(2)}`,
                matrix.maxCorrelation >= 0.7
                  ? 'Высокое перекрытие — factor crowding'
                  : matrix.maxCorrelation >= 0.4
                    ? 'Умеренное перекрытие'
                    : 'Подбор книг хорошо диверсифицирован',
              ]}
            >
              <span
                style={{
                  color: corrColor(matrix.maxCorrelation),
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 10,
                }}
              >
                max {matrix.maxCorrelation.toFixed(2)}
              </span>
            </Tooltip>
          ) : null
        }
      />
      {!showBody ? null : (
      <>
      {/* Grid: pod label column on the left, header row at the top */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `24px repeat(${matrix.podIds.length}, 1fr)`,
          gap: 1,
          fontSize: 8,
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        {/* Top-left empty corner */}
        <span />
        {/* Column header row */}
        {labels.map((lbl, j) => (
          <Tooltip key={`col-${matrix.podIds[j]}`} content={podNames[matrix.podIds[j]!] ?? ''}>
            <span
              style={{
                color: '#8b949e',
                textAlign: 'center',
                lineHeight: `${cellHeight}px`,
              }}
            >
              {lbl}
            </span>
          </Tooltip>
        ))}
        {/* Body rows */}
        {matrix.podIds.map((podA, i) => (
          <CorrelationRow
            key={`row-${podA}`}
            label={labels[i]!}
            podAName={podNames[podA] ?? podA}
            row={matrix.cells[i]!}
            podIds={matrix.podIds}
            podNames={podNames}
            rowIndex={i}
            cellHeight={cellHeight}
          />
        ))}
      </div>
      {/* Top pairs list -- gives a textual ranking of the worst */}
      {matrix.topPairs.length > 0 ? (
        <div style={{ fontSize: 10, color: '#8b949e', marginTop: 2 }}>
          {matrix.topPairs.slice(0, 3).map((pair) => {
            const a = shortPodLabel(podNames[pair.podA] ?? pair.podA);
            const b = shortPodLabel(podNames[pair.podB] ?? pair.podB);
            const sharedSummary = pair.sharedTickers
              .slice(0, 4)
              .map((s) => s.ticker)
              .join(', ');
            return (
              <Tooltip
                key={`${pair.podA}-${pair.podB}`}
                as="div"
                content={[
                  `${podNames[pair.podA] ?? pair.podA} ↔ ${podNames[pair.podB] ?? pair.podB}`,
                  `Correlation: ${pair.correlation.toFixed(2)}`,
                  pair.sharedTickers.length > 0
                    ? `Общие тикеры (${pair.sharedTickers.length}): ${pair.sharedTickers.map((s) => s.ticker).join(', ')}`
                    : '',
                ]}
                maxWidth={320}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  alignItems: 'baseline',
                  gap: 6,
                  padding: '2px 4px',
                  borderRadius: 3,
                  background: '#161b22',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: corrColor(pair.correlation),
                    alignSelf: 'center',
                  }}
                />
                <span
                  style={{
                    color: '#c9d1d9',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 10,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a}↔{b}
                  {sharedSummary ? (
                    <span style={{ color: '#6e7681', marginLeft: 4 }}>
                      · {sharedSummary}
                    </span>
                  ) : null}
                </span>
                <span
                  style={{
                    color: corrColor(pair.correlation),
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {pair.correlation >= 0 ? '+' : ''}
                  {pair.correlation.toFixed(2)}
                </span>
              </Tooltip>
            );
          })}
        </div>
      ) : null}
      </>)}
    </section>
  );
}

function CorrelationRow({
  label,
  podAName,
  row,
  podIds,
  podNames,
  rowIndex,
  cellHeight,
}: {
  label: string;
  podAName: string;
  row: CorrelationMatrix['cells'][number];
  podIds: CorrelationMatrix['podIds'];
  podNames: Record<string, string>;
  rowIndex: number;
  cellHeight: number;
}) {
  return (
    <>
      <Tooltip content={podAName}>
        <span style={{ color: '#8b949e', textAlign: 'left', lineHeight: `${cellHeight}px` }}>
          {label}
        </span>
      </Tooltip>
      {row.map((cell, j) => {
        const isDiagonal = rowIndex === j;
        const color = isDiagonal ? '#161b22' : corrColor(cell.correlation);
        const podB = podIds[j]!;
        const podBName = podNames[podB] ?? podB;
        const tipLines: string[] = [
          `${podAName} ↔ ${podBName}`,
          cell.correlation == null
            ? 'Нет данных (один из подов без позиций)'
            : `Correlation: ${cell.correlation.toFixed(3)}`,
        ];
        if (cell.sharedTickers.length > 0) {
          tipLines.push(`Общие тикеры (${cell.sharedTickers.length}):`);
          for (const s of cell.sharedTickers.slice(0, 6)) {
            tipLines.push(
              `  · ${s.ticker}: ${s.weightA >= 0 ? '+' : ''}${(s.weightA * 100).toFixed(1)}% / ${s.weightB >= 0 ? '+' : ''}${(s.weightB * 100).toFixed(1)}%`,
            );
          }
        }
        return (
          <Tooltip
            key={`cell-${rowIndex}-${j}`}
            content={tipLines}
            maxWidth={320}
            style={{
              background: color,
              borderRadius: 2,
              height: cellHeight,
              display: 'block',
            }}
          >
            <span style={{ display: 'block', width: '100%', height: '100%' }} />
          </Tooltip>
        );
      })}
    </>
  );
}

// =================================================================
// Section 7: Capital flow timeline
// =================================================================

export function CapitalFlowSection({
  events,
  collapsible,
}: {
  events: CapitalFlowEvent[];
  collapsible: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const showBody = !collapsible || expanded;
  const toggle = collapsible ? () => setExpanded((e) => !e) : undefined;
  if (events.length === 0) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          title="Capital flow"
          tooltip="История перераспределения капитала центр-буком (события centerbook.allocation_changed). Обычно меняется во время daily close."
          toggle={toggle}
          expanded={expanded}
        />
        {showBody ? (
          <div style={{ fontSize: 10, color: '#6e7681' }}>
            За последнее время перетоков капитала не было.
          </div>
        ) : null}
      </section>
    );
  }
  const nowMs = Date.now();
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <SectionHeader
        title="Capital flow"
        tooltip="Перераспределение капитала центр-буком. Зелёный ↑ = под получил больше денег (победитель); красный ↓ = срезали (underperformance). Бар отражает Δ% относительно прежнего капитала."
        toggle={toggle}
        expanded={expanded}
        right={
          collapsible ? (
            <span
              style={{
                color: '#8b949e',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
              }}
            >
              {events.length}
            </span>
          ) : null
        }
      />
      {showBody ? (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {events.map((ev, idx) => (
            <CapitalFlowRow key={`${ev.podId}-${ev.atMs}-${idx}`} event={ev} nowMs={nowMs} />
          ))}
        </ol>
      ) : null}
    </section>
  );
}

function CapitalFlowRow({ event, nowMs }: { event: CapitalFlowEvent; nowMs: number }) {
  const delta = event.deltaPct;
  const accent = delta == null ? '#8b949e' : delta > 0 ? '#3fb950' : delta < 0 ? '#f85149' : '#8b949e';
  const ageMs = nowMs - event.atMs;
  const ageLabel = formatRelativeAge(ageMs);
  const tipLines = [
    event.podName,
    event.capitalBefore != null
      ? `₽${formatMillions(event.capitalBefore)} → ₽${formatMillions(event.capitalAfter)}`
      : `Новый капитал: ₽${formatMillions(event.capitalAfter)}`,
    delta != null ? `Δ ${signedPct(delta)}` : '',
    event.reason ? `Причина: ${event.reason}` : '',
    ageLabel ? `${ageLabel} назад` : '',
  ];
  return (
    <Tooltip
      content={tipLines}
      as="div"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        alignItems: 'baseline',
        gap: 6,
        padding: '2px 4px',
        borderRadius: 3,
        background: '#161b22',
        fontSize: 10,
      }}
    >
      <span
        style={{
          color: '#c9d1d9',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <span
          aria-hidden
          style={{ color: accent, fontWeight: 700, marginRight: 4 }}
        >
          {delta == null ? '·' : delta > 0 ? '↑' : delta < 0 ? '↓' : '·'}
        </span>
        {event.podName}
      </span>
      <span
        style={{
          color: accent,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {delta != null ? signedPct(delta) : 'new'}
      </span>
      <span style={{ color: '#6e7681', fontSize: 9 }}>{ageLabel}</span>
    </Tooltip>
  );
}

function formatRelativeAge(ms: number): string {
  if (ms < 60_000) return Math.max(0, Math.round(ms / 1000)) + 's';
  if (ms < 3_600_000) return Math.round(ms / 60_000) + 'm';
  if (ms < 86_400_000) return Math.round(ms / 3_600_000) + 'h';
  return Math.round(ms / 86_400_000) + 'd';
}

// =================================================================
// Shared section header
// =================================================================

function SectionHeader({
  title,
  tooltip,
  right,
  toggle,
  expanded,
}: {
  title: string;
  tooltip: string;
  right?: React.ReactNode;
  /** When provided, the header becomes a clickable toggle and a
   *  caret indicates expand/collapse state.  Used on mobile to fold
   *  dense widgets so the sidebar doesn't overflow.  ``| undefined``
   *  needed because ``exactOptionalPropertyTypes`` requires the type
   *  to accept the value callers actually pass when the section is
   *  not collapsible. */
  toggle?: (() => void) | undefined;
  expanded?: boolean | undefined;
}) {
  const collapsible = toggle !== undefined;
  const caret = collapsible ? (expanded ? '▾' : '▸') : null;
  const labelNode = (
    <Tooltip
      content={tooltip}
      maxWidth={320}
      style={{ cursor: collapsible ? 'pointer' : 'help' }}
    >
      <span
        style={{
          color: '#8b949e',
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {caret ? (
          <span aria-hidden style={{ marginRight: 4, color: '#6e7681' }}>
            {caret}
          </span>
        ) : null}
        {title}
      </span>
    </Tooltip>
  );
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 6,
      }}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded ?? true}
          // Tap target on mobile -- 24px min row keeps the chip
          // comfortable to hit with a thumb on a 375px viewport.
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'baseline',
            flex: 1,
            minWidth: 0,
            minHeight: 24,
          }}
        >
          {labelNode}
        </button>
      ) : (
        labelNode
      )}
      {right}
    </header>
  );
}
